import { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../../shared/hooks';

interface Post {
  id: string;
  is_hidden: boolean;
}

interface UsePostOperationsReturn {
  deletePost: (id: string) => Promise<void>;
  toggleVisibility: (post: Post) => Promise<void>;
  isProcessing: boolean;
}

export const usePostOperations = (paginationKey: any): UsePostOperationsReturn => {

  const [isProcessing, setIsProcessing] = useState(false);
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  const toast = useToast();

  const showToast = (title: string) => {
    toast.closeAll();
    toast({
      title: "Success",
      description: title,
      status: "success",
      isClosable: true,
      duration: 2000,
    });
  };

  const handleError = (error: unknown) => {
    console.error(error);
    toast({
      title: "Error",
      description: "An error occurred. Please try again.",
      status: "error",
      isClosable: true,
      duration: 3000,
    });
  };

  const deletePost = async (id: string): Promise<void> => {
    try {
      setIsProcessing(true);
      const response = await apiClient.deletePost(id);

      
      if (response.status) {
        showToast("Post Deleted Successfully");
        await queryClient.invalidateQueries(["post-list", paginationKey]);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleVisibility = async (post: Post): Promise<void> => {
    try {
      setIsProcessing(true);
      const response = await apiClient.togglePostVisibility(post.id);

      if (response.status) {
        showToast("Post Visibility Toggled Successfully");
        await queryClient.refetchQueries(["post-list", paginationKey]);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    deletePost,
    toggleVisibility,
    isProcessing
  };
};
